// Modal triggers are Rails UJS remote links, so the browser offers "Open in New
// Tab/Window/Private Window/Split View" for them. Following any of those loads
// the bare `.modal` response as a full page, outside the application layout.
//
// Dropping the href takes those context menu options away. UJS keeps working
// because it matches `a[data-remote]` regardless of href — it only needs to
// find the URL, which is why `Rails.href` is taught to read `data-url` here.

import Rails from '@rails/ujs'

const REMOTE_LINK_SELECTOR = 'a[data-remote="true"][href]'
const OPENS_MODAL_PATTERN = /ajax:success->[\w-]*modal[\w-]*#open/

const railsHref = Rails.href
Rails.href = (element) => element.dataset.url || railsHref(element)

export const modalLinkUrl = (element) => element.dataset.url || element.getAttribute('href')

export const setModalLinkUrl = (element, url) => {
  element.dataset.url = url
  element.removeAttribute('href')
}

const opensModal = (element) => OPENS_MODAL_PATTERN.test(element.getAttribute('data-action') || '')

const detachHref = (element) => {
  setModalLinkUrl(element, element.getAttribute('href'))
  element.setAttribute('role', 'button')
  element.setAttribute('tabindex', '0')
}

const detachHrefsIn = (root) => {
  if (root.nodeType !== Node.ELEMENT_NODE) return

  if (root.matches(REMOTE_LINK_SELECTOR) && opensModal(root)) detachHref(root)
  root.querySelectorAll(REMOTE_LINK_SELECTOR).forEach((element) => {
    if (opensModal(element)) detachHref(element)
  })
}

// An anchor without an href is no longer activated by the keyboard, so Enter
// and Space have to be forwarded as a click for UJS to pick them up.
const forwardKeyboardActivation = (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return

  const element = event.target.closest?.('a[role="button"][data-url]')
  if (!element) return

  event.preventDefault()
  element.click()
}

const observer = new MutationObserver((mutations) => {
  mutations.forEach(({ addedNodes }) => addedNodes.forEach(detachHrefsIn))
})

// Turbo Drive swaps the whole body, so the observer is re-attached on every visit.
const start = () => {
  detachHrefsIn(document.body)
  observer.disconnect()
  observer.observe(document.body, { childList: true, subtree: true })
}

document.addEventListener('keydown', forwardKeyboardActivation)
document.addEventListener('turbo:load', start)

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start)
} else {
  start()
}
